import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const TwilioNotificationContext = createContext(null);

export const useTwilioNotifications = () => useContext(TwilioNotificationContext);

export const TwilioNotificationProvider = ({ children }) => {
  const { api, user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [activeExternalCall, setActiveExternalCall] = useState(null);
  const [incomingCallNotification, setIncomingCallNotification] = useState(null);
  const pollRef = useRef(null);
  const activeCallPollRef = useRef(null);
  const lastNotificationIds = useRef(new Set());
  // Suppression window: after the user manually dismisses the active-call UI,
  // ignore `/twilio/active-calls` poll results for 30s so a stale DB record
  // can't re-open the UI before it's been updated to completed.
  const dismissedUntilRef = useRef(0);

  // Poll for notifications
  const pollNotifications = useCallback(async () => {
    if (!isAuthenticated || !api) return;
    try {
      const res = await api.get('/twilio/notifications');
      const newNotifications = res.data.notifications || [];
      setNotifications(newNotifications);
      
      // Show toast for truly new notifications
      newNotifications.forEach(n => {
        if (!lastNotificationIds.current.has(n.id)) {
          lastNotificationIds.current.add(n.id);
          
          if (n.type === 'incoming_call') {
            setIncomingCallNotification(n);
            toast.info(n.title, { duration: 10000 });
          } else if (n.type === 'incoming_sms') {
            toast.info(n.title, { 
              description: n.data?.body?.slice(0, 60),
              duration: 5000 
            });
          } else if (n.type === 'missed_call') {
            toast.warning(n.title, { duration: 5000 });
          }
        }
      });
    } catch (error) {
      // Silently fail on polling errors
    }
  }, [isAuthenticated, api]);

  // Poll for active calls
  //
  // CRITICAL: this poll must NEVER auto-show a stale call.
  // It only:
  //   • PATCHES an already-visible call with fresher data (e.g.,
  //     third_party_active, on_hold flags), AND
  //   • CLEARS the visible call if its specific twilio_call_sid is no
  //     longer in the response.
  // Adopting a NEW call from a poll is what was making the active-call
  // UI randomly pop up while not on a call (orphan in-progress rows in
  // the DB) and disappear mid-call (transient DB transitions during
  // transfers). Visible call must be set deterministically from a user
  // action: outbound place-call OR incoming accept (both call
  // setOutboundCall directly).
  const pollActiveCalls = useCallback(async () => {
    if (!isAuthenticated || !api) return;
    if (Date.now() < dismissedUntilRef.current) return;
    try {
      const res = await api.get('/twilio/active-calls');
      const calls = res.data.calls || [];
      setActiveExternalCall((prev) => {
        if (!prev) return prev; // Never auto-adopt — see comment above
        if (prev._sdkCall) return prev; // SDK-driven outbound, no DB row yet
        const sid = prev.twilio_call_sid || prev.call_sid;
        const match = calls.find((c) => c.twilio_call_sid === sid);
        if (!match) return null; // Our call is gone → clear UI
        // Patch with latest server-side fields (hold/3-way/etc.) but
        // preserve any client-only fields (e.g., _sdkCall ref).
        return { ...prev, ...match };
      });
    } catch (error) {
      // Silently fail
    }
  }, [isAuthenticated, api]);

  useEffect(() => {
    if (isAuthenticated && user) {
      // Start polling
      pollNotifications();
      pollActiveCalls();
      
      pollRef.current = setInterval(pollNotifications, 5000); // Poll every 5s
      activeCallPollRef.current = setInterval(pollActiveCalls, 3000); // Poll every 3s for calls
      
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
        if (activeCallPollRef.current) clearInterval(activeCallPollRef.current);
      };
    } else {
      // Clear on logout
      setNotifications([]);
      setActiveExternalCall(null);
      lastNotificationIds.current.clear();
    }
  }, [isAuthenticated, user, pollNotifications, pollActiveCalls]);

  const markNotificationRead = async (notificationId) => {
    try {
      await api.post(`/twilio/notifications/${notificationId}/read`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      lastNotificationIds.current.delete(notificationId);
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/twilio/notifications/read-all');
      setNotifications([]);
    } catch (error) {
      console.error('Failed to mark all read:', error);
    }
  };

  const dismissIncomingCall = () => {
    if (incomingCallNotification) {
      markNotificationRead(incomingCallNotification.id);
      setIncomingCallNotification(null);
    }
  };

  // Track outbound call started from the UI
  const setOutboundCall = (callData) => {
    setActiveExternalCall(callData);
  };

  // Patch fields onto the active-call object without throwing away SDK refs.
  const patchActiveCall = (patch) => {
    setActiveExternalCall((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const clearActiveCall = () => {
    // Clear LOCAL UI state only. Do NOT POST /end here — that was the
    // bug that dropped Complete-Transfer'd conferences (B↔C still live
    // after A leaves; the prior auto-/end killed the entire conference
    // and kicked everyone). The 30s suppression below + the strictly
    // patch-only poll are enough to keep stale records from re-opening
    // the UI without nuking live conferences.
    setActiveExternalCall(null);
    dismissedUntilRef.current = Date.now() + 30_000;
  };

  return (
    <TwilioNotificationContext.Provider value={{
      notifications,
      activeExternalCall,
      incomingCallNotification,
      markNotificationRead,
      markAllRead,
      dismissIncomingCall,
      setOutboundCall,
      patchActiveCall,
      clearActiveCall,
      pollActiveCalls,
      unreadCount: notifications.length
    }}>
      {children}
    </TwilioNotificationContext.Provider>
  );
};
